import React, { useState, useEffect } from 'react';
import {
  Card,
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Tag,
  Space,
  Typography,
  Popconfirm,
  message,
  Upload,
  Radio
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  ToolOutlined,
  PlayCircleOutlined,
  UploadOutlined,
  GlobalOutlined
} from '@ant-design/icons';
import { careerService } from '../services/careerService';

const { Title } = Typography;
const { Option } = Select;
const { TextArea } = Input;

const Career = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [languageFilter, setLanguageFilter] = useState('all'); // 'all', 'ru', 'tg'

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [form] = Form.useForm();

  // Загрузка материалов из Firebase
  useEffect(() => {
    loadMaterials();
  }, []);

  const loadMaterials = async () => {
    try {
      setLoading(true);
      const data = await careerService.getAllMaterials();
      setMaterials(data);
    } catch (error) {
      console.error('Error loading materials:', error);
      message.error('Ошибка загрузки материалов');
    } finally {
      setLoading(false);
    }
  };

  // Фильтрованные материалы по языку
  const filteredMaterials = languageFilter === 'all'
    ? materials
    : materials.filter(m => (m.language || 'ru') === languageFilter);

  const columns = [
    {
      title: 'Материал',
      dataIndex: 'title',
      key: 'title',
      render: (text, record) => (
        <Space>
          {record.type === 'Видео' ? (
            <PlayCircleOutlined style={{ fontSize: '24px', color: '#FF4D4F' }} />
          ) : (
            <ToolOutlined style={{ fontSize: '24px', color: '#2196F3' }} />
          )}
          <div>
            <div style={{ fontWeight: 'bold' }}>{text}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.description}</div>
          </div>
        </Space>
      ),
    },
    {
      title: 'Тип',
      dataIndex: 'type',
      key: 'type',
      render: (type) => (
        <Tag color={type === 'Видео' ? 'red' : 'blue'}>{type}</Tag>
      )
    },
    {
      title: 'Язык',
      dataIndex: 'language',
      key: 'language',
      width: 120,
      render: (language) => {
        const lang = language || 'ru';
        const config = {
          ru: { color: 'blue', text: '🇷🇺 Русский' },
          tg: { color: 'green', text: '🇹🇯 Тоҷикӣ' }
        };
        const langConfig = config[lang] || config.ru;
        return <Tag color={langConfig.color}>{langConfig.text}</Tag>;
      },
      filters: [
        { text: '🇷🇺 Русский', value: 'ru' },
        { text: '🇹🇯 Тоҷикӣ', value: 'tg' },
      ],
      onFilter: (value, record) => (record.language || 'ru') === value,
    },
    {
      title: 'Статус',
      dataIndex: 'status',
      key: 'status',
      render: (status) => {
        const statusConfig = {
          active: { color: 'green', text: 'Активен' },
          draft: { color: 'orange', text: 'Черновик' },
          archived: { color: 'red', text: 'Архив' }
        };
        const config = statusConfig[status] || { color: 'default', text: status };
        return <Tag color={config.color}>{config.text}</Tag>;
      }
    },
    {
      title: 'Просмотры',
      dataIndex: 'views',
      key: 'views',
      render: (views) => `${(views || 0).toLocaleString()}`
    },
    {
      title: 'Длительность',
      dataIndex: 'duration',
      key: 'duration',
    },
    {
      title: 'Автор',
      dataIndex: 'author',
      key: 'author',
    },
    {
      title: 'Действия',
      key: 'actions',
      render: (_, record) => (
        <Space>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => handleView(record)}
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
          />
          <Popconfirm
            title="Удалить материал?"
            onConfirm={() => handleDelete(record.id)}
            okText="Да"
            cancelText="Нет"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  const handleAdd = () => {
    setEditingMaterial(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    form.setFieldsValue({
      ...material,
      language: material.language || 'ru',
    });
    setIsModalVisible(true);
  };

  const handleView = (material) => {
    const langLabel = (material.language || 'ru') === 'tg' ? '🇹🇯 Тоҷикӣ' : '🇷🇺 Русский';
    Modal.info({
      title: material.title,
      content: (
        <div>
          <p><strong>Описание:</strong> {material.description}</p>
          <p><strong>Тип:</strong> {material.type}</p>
          <p><strong>Язык:</strong> {langLabel}</p>
          <p><strong>Статус:</strong> {material.status}</p>
          <p><strong>Просмотры:</strong> {(material.views || 0).toLocaleString()}</p>
          <p><strong>Длительность:</strong> {material.duration || '-'}</p>
          <p><strong>Автор:</strong> {material.author || 'Администратор'}</p>
          <p><strong>Дата создания:</strong> {
            material.createdAt ? (
              material.createdAt.toDate ? 
                material.createdAt.toDate().toLocaleDateString('ru-RU') : 
                new Date(material.createdAt).toLocaleDateString('ru-RU')
            ) : '-'
          }</p>
        </div>
      ),
    });
  };

  const handleDelete = async (id) => {
    try {
      await careerService.deleteMaterial(id);
      setMaterials(materials.filter(material => material.id !== id));
      message.success('Материал удален');
    } catch (error) {
      console.error('Error deleting material:', error);
      message.error('Ошибка удаления материала');
    }
  };

  const handleModalOk = async () => {
    try {
      const values = await form.validateFields();
      
      // Исключаем файл из данных для Firestore (File объекты не поддерживаются)
      const { file, ...materialData } = values;
      
      // Если есть файл, извлекаем имя и размер
      if (file) {
        let fileObj = null;
        
        // Проверяем разные структуры данных из Upload компонента
        if (file.fileList && file.fileList.length > 0) {
          fileObj = file.fileList[0].originFileObj || file.fileList[0];
        } else if (file.file) {
          fileObj = file.file.originFileObj || file.file;
        } else if (file.originFileObj) {
          fileObj = file.originFileObj;
        } else if (file instanceof File) {
          fileObj = file;
        }
        
        if (fileObj && fileObj.name) {
          materialData.fileName = fileObj.name;
          if (fileObj.size) {
            materialData.fileSize = `${(fileObj.size / 1024 / 1024).toFixed(2)} MB`;
          }
        }
      }
      
      if (editingMaterial) {
        // Обновление существующего материала
        await careerService.updateMaterial(editingMaterial.id, materialData);
        message.success('Материал обновлен');
        await loadMaterials(); // Перезагружаем список
      } else {
        // Создание нового материала
        await careerService.createMaterial(materialData);
        message.success('Материал добавлен');
        await loadMaterials(); // Перезагружаем список
      }
      
      setIsModalVisible(false);
      form.resetFields();
      setEditingMaterial(null);
    } catch (error) {
      console.error('Error saving material:', error);
      if (error.message) {
        message.error(error.message);
      } else {
        message.error('Ошибка сохранения материала');
      }
    }
  };

  const handleModalCancel = () => {
    setIsModalVisible(false);
    form.resetFields();
    setEditingMaterial(null);
  };

  // Подсчёт материалов по языкам
  const ruCount = materials.filter(m => (m.language || 'ru') === 'ru').length;
  const tgCount = materials.filter(m => (m.language || 'ru') === 'tg').length;

  return (
    <div>
      <div style={{ marginBottom: '24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div>
          <Title level={2}>Управление материалами развития</Title>
          <p style={{ color: '#666', margin: 0 }}>Создавайте и редактируйте материалы по профориентации</p>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleAdd}
        >
          Добавить материал
        </Button>
      </div>

      {/* Фильтр по языку */}
      <Card style={{ marginBottom: '16px' }}>
        <Space size="middle" align="center">
          <GlobalOutlined style={{ fontSize: '18px', color: '#1890ff' }} />
          <span style={{ fontWeight: 'bold', fontSize: '14px' }}>Фильтр по языку:</span>
          <Radio.Group
            value={languageFilter}
            onChange={(e) => setLanguageFilter(e.target.value)}
            buttonStyle="solid"
          >
            <Radio.Button value="all">
              Все ({materials.length})
            </Radio.Button>
            <Radio.Button value="ru">
              🇷🇺 Русский ({ruCount})
            </Radio.Button>
            <Radio.Button value="tg">
              🇹🇯 Тоҷикӣ ({tgCount})
            </Radio.Button>
          </Radio.Group>
        </Space>
      </Card>

      <Card>
        <Table
          columns={columns}
          dataSource={filteredMaterials}
          rowKey="id"
          loading={loading}
          pagination={{
            pageSize: 10,
            showSizeChanger: true,
            showQuickJumper: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} из ${total} материалов`
          }}
        />
      </Card>

      <Modal
        title={editingMaterial ? 'Редактировать материал развития' : 'Добавить материал развития'}
        open={isModalVisible}
        onOk={handleModalOk}
        onCancel={handleModalCancel}
        width={600}
      >
        <Form
          form={form}
          layout="vertical"
          initialValues={{ language: 'ru' }}
        >
          {/* Выбор языка — первым */}
          <Form.Item
            name="language"
            label="Язык материала"
            rules={[{ required: true, message: 'Выберите язык' }]}
            tooltip="Выберите, на каком языке материал. В приложении он будет показан пользователям с соответствующим языком."
          >
            <Radio.Group buttonStyle="solid" size="large">
              <Radio.Button value="ru" style={{ height: '48px', lineHeight: '48px', fontSize: '16px' }}>
                🇷🇺 Русский
              </Radio.Button>
              <Radio.Button value="tg" style={{ height: '48px', lineHeight: '48px', fontSize: '16px' }}>
                🇹🇯 Тоҷикӣ
              </Radio.Button>
            </Radio.Group>
          </Form.Item>

          <Form.Item
            name="title"
            label="Название материала"
            rules={[{ required: true, message: 'Введите название' }]}
          >
            <Input placeholder="Введите название материала" />
          </Form.Item>

          <Form.Item
            name="description"
            label="Описание"
            rules={[{ required: true, message: 'Введите описание' }]}
          >
            <TextArea rows={3} placeholder="Краткое описание материала" />
          </Form.Item>

          <Form.Item
            name="type"
            label="Тип материала"
            rules={[{ required: true, message: 'Выберите тип' }]}
          >
            <Select placeholder="Выберите тип материала">
              <Option value="Видео">Видео</Option>
              <Option value="Презентация">Презентация</Option>
              <Option value="Статья">Статья</Option>
              <Option value="Тест">Тест</Option>
              <Option value="Инфографика">Инфографика</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="duration"
            label="Длительность"
            rules={[{ required: true, message: 'Введите длительность' }]}
          >
            <Input placeholder="Например: 15:30" />
          </Form.Item>

          <Form.Item
            name="videoUrl"
            label="Ссылка на YouTube видео"
            tooltip="Вставьте ссылку на YouTube видео (например: https://www.youtube.com/watch?v=VIDEO_ID или https://youtu.be/VIDEO_ID)"
          >
            <Input placeholder="https://www.youtube.com/watch?v=..." />
          </Form.Item>

          <Form.Item
            name="status"
            label="Статус"
            rules={[{ required: true, message: 'Выберите статус' }]}
          >
            <Select placeholder="Выберите статус">
              <Option value="draft">Черновик</Option>
              <Option value="active">Активен</Option>
              <Option value="archived">Архив</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="file"
            label="Файл материала"
          >
            <Upload.Dragger
              name="file"
              multiple={false}
              beforeUpload={() => false}
            >
              <p className="ant-upload-drag-icon">
                <UploadOutlined />
              </p>
              <p className="ant-upload-text">Нажмите или перетащите файл сюда</p>
              <p className="ant-upload-hint">
                Поддерживаются форматы: MP4, PDF, PPT, DOC
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Career;
